/** Admin顶部栏 — 56px, 白色, 面包屑+搜索+操作按钮 */
import { Box, Typography, InputBase, IconButton, Badge, Tooltip, Menu, MenuItem, Avatar, ButtonBase, Button, Divider } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMemo, useState, useCallback, useEffect, type MouseEvent } from 'react';
import { useAppContext } from '@/contexts/AppContext';
import { COLORS } from '@/theme/colors';
import { containerApi, type NotificationItem } from '@/api/container';
import { useTodoCount } from '@/hooks/useTodoCount';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SearchIcon from '@mui/icons-material/Search';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import AssignmentIcon from '@mui/icons-material/Assignment';

const routeLabels: Record<string, string> = {
  '/admin/dashboard': '全局 Dashboard',
  '/admin/product-category': '商品与类目管理',
  '/admin/trade-orders': '交易与订单管理',
  '/admin/finance-receivables': '财务与收款管理',
  '/admin/container-archive': '柜档案与履约追踪',
  '/admin/shipping-booking': '船务与订舱管理',
  '/admin/customs-clearance': '报关与清关管理',
  '/admin/document-verification': '单证一致性校验台',
  '/admin/iot-alerts': '设备数据监控',
  '/admin/iot-alert-center': '告警中心',
  '/admin/claim-rules': 'BluGuard 理赔中心',
  '/admin/risk-rating': '风控与买家评级',
  '/system/config': '系统配置与数据字典',
  '/system/rbac-audit': 'RBAC 与审计日志',
  '/system/ai-copilot': 'AI Copilot 对话',
  '/system/public-verify': '公共验真',
  '/system/user': '用户管理',
  '/system/role': '角色管理',
  '/system/menu': '菜单管理',
  '/system/org': '租户管理',
  '/mobile/task-pool': 'E01 角色登录与任务池',
  '/mobile/scan-bind-iot': 'E02 扫码绑定 IoT',
  '/mobile/loading-seal': 'E04 装柜与铅封记录',
  '/mobile/node-inspection': 'E03+E05 节点与质检上报',
  '/mobile/sign-unbox': 'E06 目的港签收与开箱',
  '/buyer/order-payment': 'A07+A08 订单与付款中心',
  '/buyer/fulfillment-tracking': 'A09 全链路履约追踪',
  '/buyer/document-reports': 'A10 单证与报告中心',
  '/buyer/claim-center': 'A11 BluGuard 理赔中心',
};

// 路径前缀 → 组名 (按最长前缀优先匹配)
const routeGroups: Array<[string, string]> = [
  ['/admin/dashboard', '履约中心'],
  ['/admin/trade-orders', '履约中心'],
  ['/admin/container-archive', '履约中心'],
  ['/admin/shipping-booking', '物流与 IoT'],
  ['/admin/customs-clearance', '物流与 IoT'],
  ['/admin/document-verification', '物流与 IoT'],
  ['/admin/iot-alerts', '履约中心'],
  ['/admin/product-category', '风控与保障'],
  ['/admin/finance-receivables', '风控与保障'],
  ['/admin/claim-rules', '风控与保障'],
  ['/admin/risk-rating', '风控与保障'],
  ['/system/', '系统管理'],
  ['/mobile/', 'Mobile H5 现场实操'],
  ['/buyer/', 'Buyer H5 买家协同'],
];

export default function TopBar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAppContext();
  const { count: todoCount, refresh: refreshTodoCount } = useTodoCount();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [searchValue, setSearchValue] = useState('');

  // 决策⑥：站内通知铃铛（读 NODE_TODO，跳转柜档案履约节点）
  const [notifAnchor, setNotifAnchor] = useState<null | HTMLElement>(null);
  const [notifs, setNotifs] = useState<NotificationItem[]>([]);
  const [unread, setUnread] = useState<number>(0);

  const loadNotifs = useCallback(async () => {
    try {
      const [list, count] = await Promise.all([
        containerApi.listMyNotifications('NODE_TODO'),
        containerApi.getUnreadCount('NODE_TODO'),
      ]);
      setNotifs(Array.isArray(list) ? list : []);
      setUnread(typeof count === 'number' ? count : 0);
    } catch {
      // 静默失败（无通知权限 / 未登录 / 网关未放行均不影响主流程）
    }
  }, []);

  useEffect(() => {
    loadNotifs();
  }, [loadNotifs]);

  const handleMenuOpen = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleSettings = () => {
    handleMenuClose();
    alert('功能开发中');
  };

  const handleLogout = () => {
    handleMenuClose();
    logout();
    navigate('/login');
  };

  const handleSearch = useCallback(() => {
    if (searchValue.trim()) {
      // 跳转到交易订单页并携带搜索参数
      navigate(`/admin/trade-orders?search=${encodeURIComponent(searchValue.trim())}`);
      setSearchValue('');
    }
  }, [searchValue, navigate]);

  const handleSearchKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  }, [handleSearch]);

  const { groupName, pageName } = useMemo(() => {
    const path = location.pathname;
    const label = routeLabels[path] || path.split('/').pop() || 'Dashboard';
    let group = 'Admin 运营后台';
    for (const [prefix, name] of routeGroups) {
      if (path === prefix || path.startsWith(prefix + '/') || (prefix.endsWith('/') && path.startsWith(prefix))) {
        group = name;
        break;
      }
    }
    return { groupName: group, pageName: label };
  }, [location.pathname]);

  return (
    <Box
      sx={{
        height: 56,
        minHeight: 56,
        display: 'flex',
        alignItems: 'center',
        backgroundColor: '#FFFFFF',
        borderBottom: '1px solid #F0F2F5',
        px: 3,
        gap: 2,
        zIndex: 10,
      }}
    >
      {/* 面包屑 */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: '13px', color: COLORS.grey[500] }}>
        <span>{groupName}</span>
        <span>/</span>
        <Typography component="span" sx={{ fontSize: '13px', fontWeight: 500, color: COLORS.text.primary }}>
          {pageName}
        </Typography>
      </Box>

      {/* 弹性空间 */}
      <Box sx={{ flex: 1 }} />

      {/* 搜索框 */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          backgroundColor: COLORS.grey[50],
          borderRadius: '8px',
          px: 1.5,
          height: 36,
          width: 280,
          border: '1px solid transparent',
          '&:focus-within': {
            border: `1px solid ${COLORS.primary.main}`,
            backgroundColor: COLORS.background.paper,
          },
        }}
      >
        <SearchIcon sx={{ color: COLORS.grey[500], mr: 1, fontSize: 18 }} />
        <InputBase
          placeholder="搜索订单号 / 柜号 / 买家..."
          sx={{ flex: 1, fontSize: '13px', color: COLORS.text.primary }}
          inputProps={{ 'aria-label': 'search' }}
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          onKeyDown={handleSearchKeyDown}
        />
      </Box>

      {/* 操作按钮 */}
      <Tooltip title="通知与待办">
        <IconButton
          size="small"
          sx={{ color: COLORS.text.secondary }}
          onClick={(e: MouseEvent<HTMLElement>) => { setNotifAnchor(e.currentTarget); loadNotifs(); refreshTodoCount(); }}
        >
          <Badge badgeContent={todoCount + unread} color="error" max={99}>
            <NotificationsNoneIcon sx={{ fontSize: 20 }} />
          </Badge>
        </IconButton>
      </Tooltip>
      <Tooltip title="帮助">
        <IconButton size="small" sx={{ color: COLORS.text.secondary }}>
          <HelpOutlineIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Tooltip>
      <Tooltip title="全屏">
        <IconButton size="small" sx={{ color: COLORS.text.secondary }}>
          <FullscreenIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Tooltip>

      {/* 用户区：点击展开下拉菜单 */}
      <Box
        component={ButtonBase}
        onClick={handleMenuOpen}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          ml: 1,
          px: 1,
          py: 0.5,
          borderRadius: '8px',
          '&:hover': { backgroundColor: '#F5F7FA' },
        }}
      >
        <Avatar
          src={user.avatar || undefined}
          alt={user.name}
          sx={{
            width: 28,
            height: 28,
            bgcolor: 'primary.main',
            fontSize: '13px',
            fontWeight: 500,
          }}
        >
          {user.name.charAt(0)}
        </Avatar>
        <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#212121' }}>
          {user.name}
        </Typography>
        <KeyboardArrowDownIcon sx={{ fontSize: '18px', color: '#9E9E9E' }} />
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { mt: 1, minWidth: 160, borderRadius: '8px' } } }}
      >
        <MenuItem onClick={handleSettings} sx={{ fontSize: '13px' }}>
          个人设置
        </MenuItem>
        <MenuItem onClick={handleLogout} sx={{ fontSize: '13px' }}>
          退出登录
        </MenuItem>
      </Menu>

      {/* 决策⑥：站内通知列表（NODE_TODO，点击跳转柜档案履约节点） */}
      <Menu
        anchorEl={notifAnchor}
        open={Boolean(notifAnchor)}
        onClose={() => setNotifAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { mt: 1, width: 320, maxHeight: 480, borderRadius: '8px' } } }}
      >
        {/* 待办中心入口 */}
        <MenuItem
          onClick={() => {
            setNotifAnchor(null);
            navigate('/admin/fulfillment/todo');
          }}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            px: 2,
            py: 1.5,
            backgroundColor: todoCount > 0 ? `${COLORS.warning.main}08` : 'transparent',
            '&:hover': { backgroundColor: todoCount > 0 ? `${COLORS.warning.main}15` : '#F5F5F5' },
          }}
        >
          <AssignmentIcon sx={{ fontSize: 20, color: COLORS.warning.main }} />
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: '13px', fontWeight: 500, color: COLORS.text.primary }}>
              待办中心
            </Typography>
            <Typography sx={{ fontSize: '11px', color: COLORS.grey[500] }}>
              {todoCount > 0 ? `${todoCount} 条待处理任务` : '暂无待办'}
            </Typography>
          </Box>
          {todoCount > 0 && (
            <Box
              sx={{
                px: 1,
                py: 0.25,
                borderRadius: '10px',
                backgroundColor: COLORS.warning.main,
                color: '#FFF',
                fontSize: '11px',
                fontWeight: 600,
              }}
            >
              {todoCount}
            </Box>
          )}
        </MenuItem>

        <Divider sx={{ my: 0.5 }} />

        {/* 通知标题 */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1 }}>
          <Typography sx={{ fontSize: '12px', fontWeight: 600, color: COLORS.grey[600] }}>站内通知</Typography>
          {unread > 0 && (
            <Button
              size="small"
              onClick={async () => { await containerApi.markAllNotificationsRead('NODE_TODO'); loadNotifs(); }}
              sx={{ fontSize: '11px', textTransform: 'none' }}
            >
              全部已读
            </Button>
          )}
        </Box>

        {notifs.length === 0 ? (
          <Box sx={{ px: 2, py: 3, textAlign: 'center' }}>
            <Typography sx={{ fontSize: '12px', color: COLORS.grey[500] }}>暂无通知</Typography>
          </Box>
        ) : (
          notifs.map((n) => (
            <MenuItem
              key={n.id}
              onClick={async () => {
                if (!n.read) {
                  await containerApi.markNotificationRead(n.id).catch(() => undefined);
                }
                setNotifAnchor(null);
                navigate(n.refId ? `/admin/container-archive?orderId=${n.refId}` : '/admin/container-archive');
              }}
              sx={{ display: 'block', px: 2, py: 1, borderBottom: '1px solid #FAFAFA', whiteSpace: 'normal' }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {!n.read && <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'error.main', flexShrink: 0 }} />}
                <Typography sx={{ fontSize: '12px', fontWeight: n.read ? 400 : 600, color: '#212121' }}>{n.title}</Typography>
              </Box>
              <Typography sx={{ fontSize: '11px', color: COLORS.grey[500], mt: 0.25 }}>{n.content}</Typography>
              <Typography sx={{ fontSize: '10px', color: '#BDBDBD', mt: 0.25 }}>
                {(n.createdAt || '').replace('T', ' ').substring(0, 16)}
              </Typography>
            </MenuItem>
          ))
        )}
      </Menu>
    </Box>
  );
}
